/* eslint-disable @typescript-eslint/no-empty-function */
import { Body2, ButtonText } from 'stories/elements/Typography/Text'
import { ReactNode, useRef, useState } from 'react'
import theme from 'stories/utils/theme'
import { useToggleState } from 'lib/hooks/useToggleState'
import Stack from 'components/elements/Stack'
import styled from 'styled-components'
import { Grade } from 'interfaces/Grade'
import { ApiError } from 'services/ApiService'
import { useRouter } from 'next/router'
import { parseLocale } from 'lib/constants/localization'
import GenericFormElement from 'components/modules/UploadDetails/GenericFormElement'
import NewButton from 'components/elements/NewButton'
import _useTranslation from 'lib/hooks/_useTranslation'
import { SchoolType } from 'interfaces/SchoolType'
import { Region } from 'interfaces/Region'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import UserService from 'services/UserService'
import { storeExpansionUserSelectedRegion } from 'lib/tracking/storage'
import GradeSelectionSchoolTypes from './GradeSelectionSchoolTypes'
import GradeSelectionDialog from './GradeSelectionDialog'
import GradeSelectionGrades from './GradeSelectionGrades'
import GradeSelectionRegion from './GradeSelectionRegion'
import Input from 'components/elements/Inputs/Input'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core'
import { emptyFunction } from 'lib/utils'

const ButtonsWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    padding-top: 150px;
`

const Hidden = styled.div`
    display: none;
`

const SelectorContainer = styled.div`
    min-height: auto;
    height: 100%;
    min-height: 400px;
`

const RegionTextContainer = styled.div`
    display: flex;
    gap: 5px;
    .change-region {
        cursor: pointer;
    }
`

interface Props {
    name: string
    label: string
    defaultSchoolTypes?: SchoolType[]
    defaultGrades?: Grade[]
    onConfirm?: (selectedSchoolTypes: SchoolType[], selectedGrades: Grade[]) => void
    limitToOneSelectedItem?: boolean
    children?: ReactNode
    onSave?: (region: Region | null, schoolType: SchoolType[] | undefined, grades: Grade[], age?: number) => void
    title?: string
    showAgeInput?: boolean
    isDarkTheme?: boolean
}

const GradeSelection = (props: Props) => {
    // Others
    const [showDialog, toggleShowDialog] = useToggleState(false)
    const [userAge, setUserAge] = useState(15)
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const userLanguageMatchLocale = authenticatedUser?.primaryContentLanguage.code === parsedLocale.contentLanguageCode

    // Selected values
    const [selectedSchoolTypes, setSelectedSchoolTypes] = useState<SchoolType[] | undefined>(props.defaultSchoolTypes)

    const [selectedGrades, setSelectedGrades] = useState<Grade[]>(props.defaultGrades ?? [])
    const selectedGradeIds = selectedGrades.sort((a, b) => a.level - b.level).map((g) => g.id)

    const [selectedRegion, setSelectedRegion] = useState<Region | null>(
        userLanguageMatchLocale && authenticatedUser?.region ? authenticatedUser?.region : null
    )

    const ageRef = useRef<FormHandles>(null)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('knows')

    //REGION
    const showRegionsOptions = !selectedRegion?.isDefault

    const goBackToRegionSelect = () => {
        setSelectedGrades([])
        setSelectedSchoolTypes([])
        setSelectedRegion(null)
    }

    const handleRegionSelect = (region: Region) => {
        setSelectedRegion(region)
        if (userLanguageMatchLocale) {
            UserService.update({ regionId: region.id }, { skipStoreDispatch: true }).catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'user.update')
                } else {
                    throw error
                }
            })
        } else {
            !authenticatedUser && storeExpansionUserSelectedRegion(region.id)
        }
    }

    //SCHOOLTYPE
    const handleSchoolTypeSelect = (schoolType: SchoolType) => {
        if (props.limitToOneSelectedItem) {
            setSelectedSchoolTypes([schoolType])
            return
        }
        setSelectedSchoolTypes((schoolsTypes) =>
            schoolsTypes
                ? schoolsTypes.find((school) => school.id === schoolType.id)
                    ? schoolsTypes.filter((school) => school.id !== schoolType.id)
                    : [...schoolsTypes, schoolType]
                : [schoolType]
        )
    }

    const changeDefaultSchoolTypes = (schoolTypes: SchoolType[]) => setSelectedSchoolTypes(schoolTypes)

    //GRADE
    const toggleGrade = (grade: Grade | null) => {
        if (!grade) {
            setSelectedGrades([])
            return
        }

        if (props.limitToOneSelectedItem) {
            setSelectedGrades([grade])
            return
        }

        const isAlreadySelected = selectedGradeIds.includes(grade.id)

        if (isAlreadySelected) {
            setSelectedGrades(selectedGrades.filter((g) => g.id !== grade.id))
        } else {
            setSelectedGrades([...selectedGrades, grade])
        }
    }

    const onCloseDialog = () => {
        if (props.showAgeInput && !userAge) {
            ageRef.current?.setFieldError('userAge', tCommon('common/formValidationRequiredError'))
            return
        }
        props.onConfirm && props.onConfirm(selectedSchoolTypes ?? [], selectedGrades)
        props?.onSave &&
            props.onSave(selectedRegion, selectedSchoolTypes, selectedGrades, props.showAgeInput ? userAge : undefined)
        toggleShowDialog()
    }

    const selectedPreview = selectedGrades.map((g) => g.longName).join(', ')

    const updateAge = (age: number) => setUserAge(age)

    return (
        <>
            <GradeSelectionDialog
                open={showDialog}
                handleClose={toggleShowDialog}
                title={props.title}
                backgroundColor={props.isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white}
                headerColor={props.isDarkTheme ? theme.colors.webappDialogBackground : theme.colors.white}
                isDarkTheme={props.isDarkTheme}
            >
                <SelectorContainer>
                    {showRegionsOptions && !selectedRegion ? (
                        <GradeSelectionRegion handleRegionSelect={handleRegionSelect} isDarkTheme={props.isDarkTheme} />
                    ) : (
                        <Stack>
                            {showRegionsOptions && selectedRegion ? (
                                <RegionTextContainer>
                                    <Body2 color={props.isDarkTheme ? theme.colors.white : theme.colors.body1Black}>
                                        {t('knows/createDetailsGradeSelectionDialogChoosenRegion', {
                                            region: selectedRegion.regionName,
                                        })}
                                    </Body2>
                                    <Body2
                                        color={theme.colors.deepSeaBlue}
                                        onClick={goBackToRegionSelect}
                                        fontWeight={700}
                                        className="change-region"
                                    >
                                        ({t('knows/createDetailsGradeSelectionDialogChangeRegion')})
                                    </Body2>
                                </RegionTextContainer>
                            ) : null}
                            <GradeSelectionSchoolTypes
                                changeDefaultSchoolTypes={changeDefaultSchoolTypes}
                                selectedRegion={selectedRegion}
                                initialSchoolTypes={selectedSchoolTypes}
                                toggleSchoolTypeSelect={handleSchoolTypeSelect}
                                limitToOneSelectedItem={props.limitToOneSelectedItem}
                                isDarkTheme={props.isDarkTheme}
                            />
                            <GradeSelectionGrades
                                selectedGrades={selectedGrades}
                                selectedSchoolTypes={selectedSchoolTypes}
                                toggleGrade={toggleGrade}
                                limitToOneSelectedItem={props.limitToOneSelectedItem}
                                isDarkTheme={props.isDarkTheme}
                            />
                            {props?.showAgeInput ? (
                                <Form ref={ageRef} initialData={{ userAge: userAge }} onSubmit={emptyFunction}>
                                    <Input
                                        max={100}
                                        min={10}
                                        name="userAge"
                                        type="number"
                                        label={tCommon('common/yourAge')}
                                        placeholder={tCommon('common/yourAge')}
                                        onUpdate={updateAge}
                                        darkTheme={props.isDarkTheme}
                                    />
                                </Form>
                            ) : null}
                            <ButtonsWrapper>
                                <NewButton
                                    onClick={onCloseDialog}
                                    fullWidth
                                    borderRadius={theme.borderRadius.normal}
                                    color={theme.colors.deepSeaBlue}
                                    isWebapp={props.isDarkTheme}
                                >
                                    <ButtonText
                                        color={theme.colors.white}
                                        fontFamily="var(--font-open-sans)"
                                        fontWeight={500}
                                        fontSize={14}
                                    >
                                        {tCommon('common/confirm')}
                                    </ButtonText>
                                </NewButton>
                            </ButtonsWrapper>
                        </Stack>
                    )}
                </SelectorContainer>
            </GradeSelectionDialog>

            <Hidden>
                <GenericFormElement
                    name="schoolTypes"
                    onClick={toggleShowDialog}
                    label=""
                    placeholder=""
                    value={selectedSchoolTypes}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onUpdate={() => {}}
                    selectedPreview=""
                    isDarkTheme={props.isDarkTheme}
                />
            </Hidden>
            {props.children ? (
                <>
                    <div onClick={toggleShowDialog}>{props.children}</div>
                    <Hidden>
                        <GenericFormElement
                            data-id="grades"
                            name={props.name}
                            onClick={toggleShowDialog}
                            label={props.label}
                            placeholder={props.label}
                            value={selectedGrades}
                            selectedPreview={selectedPreview}
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                            onUpdate={() => {}}
                            isDarkTheme={props.isDarkTheme}
                        />
                    </Hidden>
                </>
            ) : (
                <GenericFormElement
                    data-id="grades"
                    name={props.name}
                    onClick={toggleShowDialog}
                    label={props.label}
                    placeholder={props.label}
                    value={selectedGrades}
                    selectedPreview={selectedPreview}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onUpdate={() => {}}
                    isDarkTheme={props.isDarkTheme}
                />
            )}
        </>
    )
}

export default GradeSelection
